import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from "../../config";

// The broadcaster form used to collect a business address block and, worse,
// payout_method / payout_details -- a bank account number typed into a plain
// text field and stored unencrypted, which nothing ever read. Payouts run
// through Stripe Connect, and Stripe collects the legal name, address, tax id
// and bank details itself when a broadcaster first wants to withdraw. Asking
// for any of it here duplicates the liability without adding a capability.

export default function RegisterVendor() {
    const [form, setForm] = useState({
        business_name: '',
        display_name: '',
        email: '',
        password: '',
        password2: '',
        public_contact: '',
    });
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [error, setError] = useState('');
    const [waitlistState, setWaitlistState] = useState(null);
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const navigate = useNavigate();

    const handleChange = (e) =>
        setForm({ ...form, [e.target.name]: e.target.value });

    async function handleSubmit(e) {
        e.preventDefault();
        setError('');

        if (form.password !== form.password2) {
            setError('Those passwords do not match.');
            return;
        }
        if (!acceptTerms) {
            setError('Please accept the terms to continue.');
            return;
        }

        setSubmitting(true);
        try {
            const res = await fetch(`${API_BASE_URL}/register-vendor`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    ...form,
                    display_name: form.display_name || form.business_name,
                    accept_terms: true,
                }),
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                // 403 with a waitlist_state means the gate refused: not on the
                // list, still pending, or declined. Each needs a different next
                // step, so pass it through rather than flattening to "failed".
                setWaitlistState(data.waitlist_state || null);
                setError(data.message || 'Registration failed.');
                setSubmitting(false);
                return;
            }
            setDone(true);
        } catch {
            setError('Network error. Your account was not created.');
        }
        setSubmitting(false);
    }

    if (done) {
        return (
            <div className="wb-page wb-page--narrow">
                <h1>Check your email</h1>
                <p className="wb-sub">
                    We sent a confirmation link to <strong>{form.email}</strong>. Once you
                    confirm, sign in and register your first screen.
                </p>
                <button className="wb-btn wb-btn--primary" onClick={() => navigate('/login')}>
                    Go to sign in
                </button>
            </div>
        );
    }

    return (
        <div className="wb-page wb-page--narrow">
            <h1>Host a screen</h1>
            <p className="wb-sub" style={{ marginBottom: '1.5rem' }}>
                Create an account, register a screen, and set your own day rate. Bank
                details are collected securely by Stripe later, when you first withdraw.
            </p>

            <div className="wb-note" style={{ marginBottom: '1.5rem' }}>
                Host sign-up is by invitation while we onboard screens a few
                sites at a time. Use the email address you joined the waitlist with —
                the invitation is tied to it. Not on the list yet?{' '}
                <Link to="/waitlist">Join here</Link>.
            </div>

            <form onSubmit={handleSubmit} className="wb-card">
                <label className="wb-field">
                    <span className="wb-label">Business name</span>
                    <input
                        className="wb-input" name="business_name" autoComplete="organization"
                        value={form.business_name} onChange={handleChange} required
                    />
                    <span className="wb-hint">Shown to advertisers browsing your screens.</span>
                </label>

                <label className="wb-field">
                    <span className="wb-label">Your name</span>
                    <input
                        className="wb-input" name="display_name" autoComplete="name"
                        placeholder="Same as business name"
                        value={form.display_name} onChange={handleChange}
                    />
                </label>

                <label className="wb-field">
                    <span className="wb-label">Email</span>
                    <input
                        className="wb-input" name="email" type="email" autoComplete="email"
                        value={form.email} onChange={handleChange} required
                    />
                    <span className="wb-hint">This is what you will sign in with.</span>
                </label>

                <label className="wb-field">
                    <span className="wb-label">Contact for advertisers <em>(optional)</em></span>
                    <input
                        className="wb-input" name="public_contact"
                        placeholder="A phone number or shop email"
                        value={form.public_contact} onChange={handleChange}
                    />
                </label>

                <label className="wb-field">
                    <span className="wb-label">Password</span>
                    <input
                        className="wb-input" name="password" type="password"
                        autoComplete="new-password"
                        value={form.password} onChange={handleChange} required
                    />
                    <span className="wb-hint">
                        At least 8 characters, with an upper and lower case letter, a number,
                        and one of @$!%*?&amp;
                    </span>
                </label>

                <label className="wb-field">
                    <span className="wb-label">Confirm password</span>
                    <input
                        className="wb-input" name="password2" type="password"
                        autoComplete="new-password"
                        value={form.password2} onChange={handleChange} required
                    />
                </label>

                <label className="wb-row" style={{ marginBottom: '1rem', alignItems: 'flex-start' }}>
                    <input
                        type="checkbox" checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                    />
                    <span style={{ flex: 1 }}>
                        I accept the <Link to="/terms">Terms of Service</Link> and{" "}
                        <Link to="/privacy">Privacy Policy</Link>.
                    </span>
                </label>

                <button
                    type="submit"
                    className="wb-btn wb-btn--primary wb-btn--block"
                    disabled={submitting}
                >
                    {submitting ? 'Creating account…' : 'Create account'}
                </button>

                {error && (
                    <div className="wb-note wb-note--danger" style={{ marginTop: '1rem' }}>
                        {error}
                        {waitlistState === 'not_joined' && (
                            <div style={{ marginTop: '0.5rem' }}>
                                <Link to="/waitlist" className="wb-btn wb-btn--primary wb-btn--sm">
                                    Join the waitlist
                                </Link>
                            </div>
                        )}
                    </div>
                )}
            </form>

            <p className="wb-sub" style={{ marginTop: '1.5rem' }}>
                Want to advertise instead? <Link to="/register/customer">Sign up here</Link>.
                Already have an account? <Link to="/login">Sign in</Link>.
            </p>
        </div>
    );
}
